import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  StyledImage,
  TextMedium,
} from "../components/common/CommonComponents";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import styled from "styled-components";
import { LoginButton } from "../components/textField";
import logo from '../assests/estate-vault-logo.jpg';
import azimLogo from '../assests/azim-logo.jpg';
import { login } from "../api/serverApi";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getValidAccessToken } from "../services/garphService";
import * as microsoftTeams from "@microsoft/teams-js";

const Background = styled.div`
  background-size: cover;
  width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background-color: #ffffff;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  width: 30%;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: ${(props) => (props.flexcol ? "column" : "row")};

  @media (min-width: 640px) {
    width: 50%;
  }
`;

// Check if running in iframe
const isInIframe = () => {
  try {
    return window !== window.top;
  } catch (e) {
    return true;
  }
};

// Check if running in Teams
const isInTeams = () => {
  try {
    return window.parent !== window.self && window.location.hostname.includes('teams.microsoft.com');
  } catch {
    return false;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cookies, setCookie] = useCookies([
    "email",
    "firstName",
    "lastName",
    "roleName",
    "roleId",
    "accessToken",
    "fullname"
  ]);
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { type } = useParams();
  const screenWidth = window.innerWidth;
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [teamsInitialized, setTeamsInitialized] = useState(false);
  const [isInTeamsContext, setIsInTeamsContext] = useState(false);
  const [teamsAuthFailed, setTeamsAuthFailed] = useState(false);
  const [hasProcessedLogin, setHasProcessedLogin] = useState(false);
  const email = accounts[0]?.username;

  // Function to set the image source based on screen size
  const setImageBasedOnScreenSize = () => {
    const screenWidth = window.innerWidth;
  };

  useEffect(() => {
    setImageBasedOnScreenSize();
    window.addEventListener("resize", setImageBasedOnScreenSize);
    // Log the current URL to help debug auth-end.html return
    console.log('Current window location:', window.location.href);
    return () => {
      window.removeEventListener("resize", setImageBasedOnScreenSize);
    };
  }, [screenWidth]);

  // Initialize Teams context
  // useEffect(() => {
  //   const initializeTeams = async () => {
  //     try {
  //       await microsoftTeams.app.initialize();
  //       setTeamsInitialized(true);

  //       // Check if we're running in Teams
  //       const context = await microsoftTeams.app.getContext();
  //       if (context && context.app?.host?.name) {
  //         setIsInTeamsContext(true);
  //         console.log('Running in Teams context:', context.app.host.name);

  //         // Only attempt automatic SSO if not already failed
  //         if (!teamsAuthFailed) {
  //           console.log('Teams detected, attempting automatic SSO...');
  //           setIsLoggingIn(true);
  //           await handleTeamsSSO();
  //         }
  //       }
  //     } catch (error) {
  //       console.log('Not running in Teams context:', error);
  //       setIsInTeamsContext(false);
  //       setTeamsInitialized(true);
  //     }
  //   };

  //   initializeTeams();
  // }, [teamsAuthFailed]);
  useEffect(() => {
    const initializeTeams = async () => {
      try {
        await microsoftTeams.app.initialize();
        setTeamsInitialized(true);

        const context = await microsoftTeams.app.getContext();

        if (context?.app?.host?.name) {
          setIsInTeamsContext(true);
          if (!hasProcessedLogin) {
            setIsLoggingIn(true);
            await handleTeamsSSO();
          }
        }
      } catch (error) {
        console.log('Not running in Teams context:', error);
        setIsInTeamsContext(false);
        setTeamsInitialized(true);
      }
    };

    if (!hasProcessedLogin && !teamsAuthFailed) {
      initializeTeams();
    }
  }, [hasProcessedLogin, teamsAuthFailed]);

  /**
   * Handle Teams SSO using Teams Authentication API
   */
  const handleTeamsSSO = async () => {
    try {
      console.log('Starting Teams SSO authentication...');
      console.log('Teams context:', isInTeamsContext);
      console.log('Teams initialized:', teamsInitialized);
      console.log('Calling microsoftTeams.authentication.getAuthToken...');
      microsoftTeams.authentication.getAuthToken({
        successCallback: async (token) => {
          console.log('Teams SSO successful - got token');
          console.log('Token length:', token ? token.length : 'No token');
          console.log('Token value (first 100 chars):', token ? token.substring(0, 100) : 'No token');
          try {
            instance.getTokenCache().clear();   // <-- ADD HERE
          } catch (e) {
            console.log("Token cache clear failed:", e);
          }
          try {
            await processLoginWithToken(token);
          } catch (error) {
            console.error('Error processing Teams token:', error);
            setLoginError('Authentication processing failed. Please try again.');
            setIsLoggingIn(false);
          }
        },
        failureCallback: (error) => {
          console.error('Teams SSO failed:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          if (error.message && error.message.includes('App resource defined in manifest and iframe origin do not match')) {
            setLoginError('Teams authentication configuration issue. Please use the login button below.');
            setTeamsAuthFailed(true);
          } else {
            setLoginError('Teams authentication failed. Please try the login button.');
          }
          setIsLoggingIn(false);
        },
        resources: ['https://graph.microsoft.com']
      });
    } catch (error) {
      console.error('Teams SSO failed:', error);
      setLoginError('Teams authentication failed. Please try the login button.');
      setTeamsAuthFailed(true);
      setIsLoggingIn(false);
    }
  };

  /**
   * Handle manual login button click
   */
  const handleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError('');

    try {
      if (isInTeamsContext) {
        // In Teams, use Teams SSO or fallback to redirect
        await handleTeamsSSO();
      } else {
        // On web, use popup
        const response = await instance.loginPopup(loginRequest);
        if (response.accessToken) {
          await processLoginWithToken(response.accessToken);
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
      setLoginError("Login failed. Please try again.");
      setIsLoggingIn(false);
    }
  };

  /**
   * Process login with token
   */
  const processLoginWithToken = async (token) => {
    console.log('processLoginWithToken called with token:', token ? token.substring(0, 100) : 'No token');
    // if (hasProcessedLogin) return;
    if (hasProcessedLogin && !isInTeamsContext) return;

    setHasProcessedLogin(true); // prevent re-processing

    try {
      if (isInTeamsContext) {
        // Exchange Teams SSO token for API access token
        const tokenResponse = await fetch("https://land-document-prod-huchesggdyh8b9b6.centralindia-01.azurewebsites.net/api/token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
          credentials: "include"
        });
        const { accessToken } = await tokenResponse.json();
        token = accessToken; // <-- Use the new access token for all further API calls
      }

      console.log("Final token being stored:", token);

      // 🔥 STORE THE TOKEN
      setCookie("accessToken", token, { secure: true, sameSite: "strict", path: "/" });

      const headers = { Authorization: `Bearer ${token}` };
      const response = await login(headers);
      setCookie("permissions", response.data.role, { secure: true, sameSite: "strict", path: "/" });

      if (microsoftTeams.app.isInitialized() && response.data.role === "End User") {
        alert("The user does not have access to the Estate Vault in Microsoft Teams.");
        // clear session
        setCookie("accessToken", "", { path: "/" });
        navigate("/message");
        return;
      }

      if (response.data?.role && response.data?.name) {
        const userEmail = response.data.email || accounts[0]?.username || 'teams-user@domain.com';

        setCookie("email", userEmail, { secure: true, sameSite: "strict", path: "/" });
        setCookie("name", response.data.name, { secure: true, sameSite: "strict", path: "/" });

        if (!isInTeamsContext) {
          setCookie("permissions", response.data.role, { secure: true, sameSite: "strict", path: "/" });
        }

       if (typeof type === "undefined") {
  setTimeout(() => {
    if (
      response.data.role === "Admin" ||
      response.data.role === "Location Admin" ||
      response.data.role === "State Admin"
    ) {
      navigate("/sitecreation");
    } else if (
      response.data.role === "End User" ||
      response.data.role === "Spoc"
    ) {
      navigate("/report");
    } else {
      navigate("/file");
    }

    if (!microsoftTeams.app.isInitialized()) {
      window.location.reload();
    }

    setIsLoggingIn(false);
  }, 500);
}
      } else {
        setLoginError("Access denied. Please contact administrator.");
        setIsLoggingIn(false);
        navigate("/message");
      }
    } catch (error) {
      console.error("Login processing failed:", error);
      setLoginError("Login processing failed. Please try again.");
      setIsLoggingIn(false);
      navigate("/message");
    }
  };


  /**
   * Handle existing authentication
   */
  const getTokenAndLogin = async () => {
    try {
      const token = await getValidAccessToken(instance, accounts);
      await processLoginWithToken(token);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Show spinner or message here
        setLoginError("Redirecting to login...");
        await instance.loginRedirect(loginRequest);
      } else {
        // Log the real error for debugging
        console.error("Token acquisition or login failed:", error);
        setLoginError("Authentication failed. Please try again.");
        setIsLoggingIn(false);
        navigate("/message");
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isInTeamsContext) {
      console.log("User is authenticated, not in Teams. Starting getTokenAndLogin.");
      setIsLoggingIn(true);
      setLoginError('');
      getTokenAndLogin();
    } else {
      console.log("User is NOT authenticated or is in Teams. isAuthenticated:", isAuthenticated, "isInTeamsContext:", isInTeamsContext);
    }
  }, [isAuthenticated, isInTeamsContext]);

  useEffect(() => {
    console.log("User cookies:", cookies);
    // Add more logs for API responses, etc.
  }, []);

  return (
    <div className="bg-[#DBE5FE]">
      <Background>
        <Card flexcol={"true"} style={{ position: "relative" }}>
          {/* Header logos */}
          <div
            style={{
              width: "100%",
              position: "relative",
              marginBottom: "2rem",
              paddingTop: "20px", // space for Azim logo only
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Top-left Azim logo */}
            <div style={{ position: "absolute", top: 0, left: 0 }}>
              <StyledImage
                width={screenWidth < 640 ? 50 : 70}
                height={screenWidth < 640 ? 50 : 70}
                src={azimLogo}
              />
            </div>

            {/* Center logo */}
            <StyledImage
              width={screenWidth < 640 ? 80 : 150}
              height={screenWidth < 640 ? 80 : 150}
              src={logo}
            />
          </div>
          <TextMedium fontSize={25}>
            {isInTeamsContext ? "Teams SSO Login" : "Login with SSO"}
          </TextMedium>

          {loginError && (
            <div style={{ marginBottom: '15px', fontSize: '14px', color: '#d32f2f', textAlign: 'center' }}>
              {loginError}
            </div>
          )}

          {/* Show button if not auto-logging in Teams or if Teams auth failed */}
          {!(isInTeamsContext && isLoggingIn && !loginError && !teamsAuthFailed) && (
            <LoginButton size="large" onClick={handleLogin} disabled={isLoggingIn || (!teamsInitialized && isInTeamsContext)}>
              {isLoggingIn ? "Signing in..." : (isInTeamsContext ? "Teams Login" : "Login")}
            </LoginButton>
          )}

          {!teamsInitialized && isInTeamsContext && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              Initializing Teams...
            </div>
          )}

          {isLoggingIn && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              {isInTeamsContext ? "Signing you in automatically..." : "Please wait while we sign you in..."}
            </div>
          )}

          {isInTeamsContext && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Running in Microsoft Teams
            </div>
          )}

          {teamsAuthFailed && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Teams SSO unavailable - using alternative authentication
            </div>
          )}
        </Card>
      </Background>
    </div>
  );
};

export default Login;

